import React, { useRef, useEffect } from 'react';
import { sanitizeHTML } from '../../utils/sanitize';

const RichTextEditor = ({ value, onChange, placeholder, className = '' }) => {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      // Sanitize content before setting
      const sanitizedValue = sanitizeHTML(value || '', {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'a'],
        ALLOWED_ATTR: ['href', 'title', 'target']
      });
      editorRef.current.innerHTML = sanitizedValue;
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      // Sanitize content before passing to parent
      const sanitizedContent = sanitizeHTML(editorRef.current.innerHTML, {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'a'],
        ALLOWED_ATTR: ['href', 'title', 'target']
      });
      onChange(sanitizedContent);
    }
  };

  const formatText = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current.focus();
    handleInput();
  };

  const insertList = (type) => {
    formatText(type === 'ol' ? 'insertOrderedList' : 'insertUnorderedList');
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
    handleInput();
  };

  return (
    <div className={`border border-border_gray rounded-lg ${className}`}>
      {/* Toolbar */}
      <div className="border-b border-border_gray p-2 flex flex-wrap gap-1">
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => formatText('bold')}
            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
            title="Bold"
          >
            <i className="fas fa-bold"></i>
          </button>
          <button
            type="button"
            onClick={() => formatText('italic')}
            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
            title="Italic"
          >
            <i className="fas fa-italic"></i>
          </button>
          <button
            type="button"
            onClick={() => formatText('underline')}
            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
            title="Underline"
          >
            <i className="fas fa-underline"></i>
          </button>
        </div>

        <div className="w-px bg-gray-300"></div>

        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => formatText('justifyLeft')}
            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
            title="Align Left"
          >
            <i className="fas fa-align-left"></i>
          </button>
          <button
            type="button"
            onClick={() => formatText('justifyCenter')}
            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
            title="Align Center"
          >
            <i className="fas fa-align-center"></i>
          </button>
          <button
            type="button"
            onClick={() => formatText('justifyRight')}
            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
            title="Align Right"
          >
            <i className="fas fa-align-right"></i>
          </button>
        </div>

        <div className="w-px bg-gray-300"></div>

        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => insertList('ul')}
            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
            title="Bullet List"
          >
            <i className="fas fa-list-ul"></i>
          </button>
          <button
            type="button"
            onClick={() => insertList('ol')}
            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
            title="Numbered List"
          >
            <i className="fas fa-list-ol"></i>
          </button>
        </div>

        <div className="w-px bg-gray-300"></div>

        <div className="flex gap-1">
          <select
            onChange={(e) => {
              if (e.target.value) {
                formatText('formatBlock', `<${e.target.value}>`);
                e.target.value = '';
              }
            }}
            className="text-sm border border-gray-300 rounded px-2 py-1"
            defaultValue=""
          >
            <option value="">Format</option>
            <option value="h1">Heading 1</option>
            <option value="h2">Heading 2</option>
            <option value="h3">Heading 3</option>
            <option value="p">Paragraph</option>
          </select>
        </div>

        <div className="w-px bg-gray-300"></div>

        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => formatText('removeFormat')}
            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
            title="Clear Formatting"
          >
            <i className="fas fa-eraser"></i>
          </button>
        </div>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        className="p-4 min-h-[200px] focus:outline-none"
        style={{ whiteSpace: 'pre-wrap' }}
        suppressContentEditableWarning={true}
        placeholder={placeholder}
      />

      {/* Character count */}
      <div className="border-t border-border_gray px-4 py-2 text-xs text-gray-500">
        Characters: {value ? value.replace(/<[^>]*>/g, '').length : 0}
      </div>
    </div>
  );
};

export default RichTextEditor;